/**
 * CalendarViewPage Component
 *
 * Top-level page for the aggregated calendar bookings view.
 * Manages state for month navigation, resource filtering, and data fetching.
 * Composes MonthNavigation, ResourceFilter, MonthGrid, and BookingsListPanel.
 *
 * Validates: Requirements 2.1, 3.1, 3.5, 4.4, 6.1, 6.2, 6.3, 6.5
 * - Requirement 2.1: Displays Month_Grid for the current month on load.
 * - Requirement 3.1: Navigation controls adjacent to the month/year header.
 * - Requirement 3.5: Loading indicator without removing grid structure.
 * - Requirement 4.4: Reloads booking data when resource filter changes.
 * - Requirement 6.1: Fetches bookings for the displayed month from all calendar-active agents.
 * - Requirement 6.2: Displays error message with retry button on fetch failure.
 * - Requirement 6.3: Retry button re-attempts the data fetch.
 * - Requirement 6.5: Loading state does not block interaction with navigation controls.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiDeleteJson, apiGet, TENANT_TOKEN_KEY } from '../../../api/client';
import { Button, Modal } from '../../../design-system';
import { tokens } from '../../../design-system/tokens';
import { showGrowl } from '../../../ui/growlState';
import { useTenantPermissions } from '../../../auth/TenantPermissionsContext';
import { useAgentSelector } from '../../../auth/AgentSelectorContext';
import type { CalendarViewBooking, CalendarViewResource } from '../calendarView.types';
import { navigateMonth, getBookingsForDay, navigateCalendarDay, isSameCalendarDay, preferInternalBookings } from '../calendarView.utils';
import { BookingsListPanel } from './BookingsListPanel';
import { MonthGrid } from './MonthGrid';
import { MonthNavigation } from './MonthNavigation';
import { ResourceFilter } from './ResourceFilter';
import { StatusFilterBar } from './StatusFilterBar';
import { DayNavigationBar } from './DayNavigationBar';
import {
  CALENDAR_VIEW_STATUS_ORDER,
  type CalendarViewFilterableStatus,
} from './calendarViewStatusOptions';

// ============================================
// HELPERS
// ============================================

function getCurrentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

// ============================================
// STYLES
// ============================================

const pageContainerStyle: React.CSSProperties = {
  maxWidth: 1080,
  margin: '0 auto',
  padding: '28px 24px 40px',
};

const headerStyle: React.CSSProperties = {
  marginBottom: 24,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '22px',
  fontWeight: 800,
  color: tokens.colors.neutral[900],
  letterSpacing: '-0.02em',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.spacing[4],
  marginBottom: tokens.spacing[5],
  flexWrap: 'wrap',
};

const gridWrapperStyle: React.CSSProperties = {
  position: 'relative',
  marginBottom: tokens.spacing[6],
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(248, 250, 252, 0.6)',
  borderRadius: tokens.borderRadius.lg,
  zIndex: 10,
  pointerEvents: 'none',
};

const spinnerStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  border: `3px solid ${tokens.colors.neutral[200]}`,
  borderTopColor: tokens.colors.primary[500],
  borderRadius: '50%',
  animation: 'calendarSpin 0.7s linear infinite',
};

const errorContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[3],
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  marginBottom: tokens.spacing[4],
  backgroundColor: tokens.colors.error.light,
  borderRadius: tokens.borderRadius.md,
  border: `1px solid ${tokens.colors.error.main}`,
};

const errorTextStyle: React.CSSProperties = {
  flex: 1,
  fontSize: tokens.typography.fontSize.sm,
  color: tokens.colors.error.dark,
};

const retryButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
  border: `1px solid ${tokens.colors.error.main}`,
  borderRadius: tokens.borderRadius.md,
  backgroundColor: 'transparent',
  color: tokens.colors.error.dark,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}`,
  whiteSpace: 'nowrap',
};

// ============================================
// MAIN COMPONENT
// ============================================

export function CalendarViewPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY);
  const nav = useNavigate();
  const { can } = useTenantPermissions();
  const { selectedAgentId } = useAgentSelector();
  const canCancelBookings = can(['web.calendar.write', 'web.agents.write']);
  const agentQs = selectedAgentId ? `&agentId=${encodeURIComponent(selectedAgentId)}` : '';

  // State: current month/year
  const [year, setYear] = useState(() => getCurrentYearMonth().year);
  const [month, setMonth] = useState(() => getCurrentYearMonth().month);

  // State: resource filter
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [resources, setResources] = useState<CalendarViewResource[]>([]);

  /** Filtro por estado (multiselect; al menos uno activo). */
  const [statusFilter, setStatusFilter] = useState<Set<CalendarViewFilterableStatus>>(
    () => new Set(['confirmed'])
  );

  /** Día seleccionado para filtrar la lista (null = todo el mes). */
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // State: bookings data
  const [bookings, setBookings] = useState<CalendarViewBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State: agents without resources (for empty state guidance)
  const [agentsWithoutResources, setAgentsWithoutResources] = useState<{ id: string; name: string }[]>([]);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CalendarViewBooking | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Fetch resources on mount
  useEffect(() => {
    if (!token) return;
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void apiGet<{ resources: CalendarViewResource[] }>(
      `/api/console/calendario/resources${qs}`,
      token
    )
      .then((data) => {
        setResources(data.resources);
        setSelectedResourceId((prev) =>
          prev && data.resources.some((r) => r.id === prev) ? prev : null
        );
        // Detect agents that have calendar active but no resources
        // If resources list is empty but calendar is active, show guidance
        if (data.resources.length === 0) {
          // Fetch agents with calendar plugin to show links
          void apiGet<{ agents: { id: string; name: string }[] }>(
            `/api/console/calendario/agents-without-resources${qs}`,
            token
          )
            .then((agentData) => setAgentsWithoutResources(agentData.agents))
            .catch(() => { /* ignore */ });
        } else {
          setAgentsWithoutResources([]);
        }
      })
      .catch(() => {
        // Silently fail — resources filter will just be empty
      });
  }, [token, selectedAgentId]);

  // Fetch bookings when year, month, or filter changes
  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      let path = `/api/console/calendario/bookings?year=${year}&month=${month}${agentQs}`;
      if (selectedResourceId) {
        path += `&resourceId=${encodeURIComponent(selectedResourceId)}`;
      }
      const data = await apiGet<{ bookings: CalendarViewBooking[] }>(path, token);
      setBookings(data.bookings);
    } catch (e) {
      setError((e as Error).message || 'Error al cargar las reservas');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [token, year, month, selectedResourceId, agentQs]);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  // Navigation handlers
  const handlePrev = useCallback(() => {
    const next = navigateMonth(year, month, 'prev');
    setYear(next.year);
    setMonth(next.month);
    setSelectedDay(null);
  }, [year, month]);

  const handleNext = useCallback(() => {
    const next = navigateMonth(year, month, 'next');
    setYear(next.year);
    setMonth(next.month);
    setSelectedDay(null);
  }, [year, month]);

  const handleToday = useCallback(() => {
    const current = getCurrentYearMonth();
    setYear(current.year);
    setMonth(current.month);
    setSelectedDay(null);
  }, []);

  // Filter handler
  const handleResourceChange = useCallback((resourceId: string | null) => {
    setSelectedResourceId(resourceId);
  }, []);

  const handleStatusToggle = useCallback((status: CalendarViewFilterableStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        if (next.size <= 1) return prev;
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const handleStatusSelectAll = useCallback(() => {
    setStatusFilter(new Set(CALENDAR_VIEW_STATUS_ORDER));
  }, []);

  const handleConfirmCancellation = useCallback(
    async () => {
      if (!token || !cancelTarget || cancelTarget.bookingOrigin === 'ota') return;
      setCancellingId(cancelTarget.id);
      try {
        await apiDeleteJson(
          `/api/agents/${cancelTarget.agentId}/calendar/bookings/${cancelTarget.id}`,
          { reason: cancelReason.trim() || undefined },
          token,
          { successMessage: 'Reserva cancelada' }
        );
        setCancelTarget(null);
        setCancelReason('');
        await fetchBookings();
      } catch (e) {
        showGrowl(e instanceof Error ? e.message : 'No se pudo cancelar la reserva', 'error');
      } finally {
        setCancellingId(null);
      }
    },
    [token, cancelTarget, cancelReason, fetchBookings]
  );

  const handleSelectDay = useCallback((date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    setSelectedDay((prev) => (prev && isSameCalendarDay(prev, d) ? null : d));
  }, []);

  const handleClearSelectedDay = useCallback(() => {
    setSelectedDay(null);
  }, []);

  const handlePrevDay = useCallback(() => {
    if (!selectedDay) return;
    const next = navigateCalendarDay(selectedDay, 'prev');
    setSelectedDay(next);
    const ny = next.getFullYear();
    const nm = next.getMonth() + 1;
    if (ny !== year || nm !== month) {
      setYear(ny);
      setMonth(nm);
    }
  }, [selectedDay, year, month]);

  const handleNextDay = useCallback(() => {
    if (!selectedDay) return;
    const next = navigateCalendarDay(selectedDay, 'next');
    setSelectedDay(next);
    const ny = next.getFullYear();
    const nm = next.getMonth() + 1;
    if (ny !== year || nm !== month) {
      setYear(ny);
      setMonth(nm);
    }
  }, [selectedDay, year, month]);

  const bookingsFilteredByStatus = useMemo(() => {
    return bookings.filter((b) => {
      const key =
        b.status === 'completed' ? 'human_supervision' : (b.status as CalendarViewBooking['status']);
      return statusFilter.has(key as CalendarViewFilterableStatus);
    });
  }, [bookings, statusFilter]);

  const listBookings = useMemo(() => {
    if (!selectedDay) return bookingsFilteredByStatus;
    return preferInternalBookings(getBookingsForDay(bookingsFilteredByStatus, selectedDay));
  }, [bookingsFilteredByStatus, selectedDay]);

  const listEmptyMessage = selectedDay
    ? 'No hay reservas en este día con los filtros aplicados.'
    : 'No hay reservas en este mes con los filtros aplicados.';

  const today = new Date();

  return (
    <div style={pageContainerStyle}>
      {/* Inline keyframes for spinner animation */}
      <style>{`@keyframes calendarSpin { to { transform: rotate(360deg); } }`}</style>

      {/* Page title */}
      <header style={headerStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <h1 style={titleStyle}>Calendario</h1>
        </div>
        <p
          style={{
            margin: `${tokens.spacing[2]} 0 0`,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[500],
            maxWidth: 720,
            lineHeight: tokens.typography.lineHeight.relaxed,
          }}
        >
          La configuración de <strong style={{ color: tokens.colors.neutral[700] }}>estados de reserva</strong> (qué puede elegir la operadora y las etiquetas en pantalla) está por{' '}
          <strong style={{ color: tokens.colors.neutral[700] }}>tipo de recurso</strong>: en cada agente, menú Calendario → Tipos de recurso → editar un tipo → sección «Estados de reserva».
        </p>
      </header>

      {/* Toolbar: MonthNavigation + ResourceFilter */}
      <div style={toolbarStyle}>
        <MonthNavigation
          year={year}
          month={month}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
        />
        <ResourceFilter
          resources={resources}
          selectedResourceId={selectedResourceId}
          onChange={handleResourceChange}
        />
        <StatusFilterBar
          selected={statusFilter}
          onToggle={handleStatusToggle}
          onSelectAll={handleStatusSelectAll}
        />
      </div>

      {/* Empty resources guidance banner */}
      {resources.length === 0 && !loading && agentsWithoutResources.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[3],
            padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
            marginBottom: tokens.spacing[4],
            backgroundColor: tokens.colors.warning.light,
            borderRadius: tokens.borderRadius.md,
            border: `1px solid ${tokens.colors.warning.main}`,
          }}
        >
          <span style={{ flex: 1, fontSize: tokens.typography.fontSize.sm, color: tokens.colors.warning.dark }}>
            No hay recursos configurados. Para usar el calendario, creá recursos en tus agentes:
            {agentsWithoutResources.map((agent, i) => (
              <button
                key={agent.id}
                type="button"
                onClick={() => nav(`/app/agents/${agent.id}/calendar/resources`)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: tokens.colors.primary[600],
                  cursor: 'pointer',
                  fontWeight: tokens.typography.fontWeight.semibold,
                  fontSize: tokens.typography.fontSize.sm,
                  textDecoration: 'underline',
                  padding: `0 ${tokens.spacing[1]}`,
                  fontFamily: 'inherit',
                }}
              >
                {agent.name}{i < agentsWithoutResources.length - 1 ? ',' : ''}
              </button>
            ))}
          </span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={errorContainerStyle}>
          <span style={errorTextStyle}>{error}</span>
          <button
            type="button"
            onClick={() => void fetchBookings()}
            style={retryButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.error.light;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Month grid with loading overlay */}
      <div style={gridWrapperStyle}>
        {loading && (
          <div style={loadingOverlayStyle}>
            <div style={spinnerStyle} />
          </div>
        )}
        <MonthGrid
          year={year}
          month={month}
          bookings={bookingsFilteredByStatus}
          today={today}
          selectedDate={selectedDay}
          onSelectDay={handleSelectDay}
        />
      </div>

      <p
        style={{
          margin: `0 0 ${tokens.spacing[4]}`,
          fontSize: tokens.typography.fontSize.xs,
          color: tokens.colors.neutral[500],
        }}
      >
        Leyenda: <span aria-hidden>💬</span> Interactive Talker (chat/directo) ·{' '}
        <span aria-hidden>▲</span> Airbnb · <span aria-hidden>🌐</span> otra OTA
      </p>

      {/* Bookings list panel */}
      <div style={{ marginTop: tokens.spacing[2] }}>
        <h2
          style={{
            margin: `0 0 ${tokens.spacing[2]}`,
            fontSize: tokens.typography.fontSize.lg,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.neutral[800],
          }}
        >
          Lista de reservas
        </h2>
        {selectedDay ? (
          <DayNavigationBar
            selectedDay={selectedDay}
            onPrevDay={handlePrevDay}
            onNextDay={handleNextDay}
            onClearDay={handleClearSelectedDay}
          />
        ) : null}
        <p
          style={{
            margin: `0 0 ${tokens.spacing[3]}`,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[600],
          }}
        >
          {selectedDay
            ? 'Solo se listan las reservas del día seleccionado. Flechas para cambiar de día; otro clic en el mismo día o «Ver todo el mes» para volver.'
            : 'Mostrando todas las reservas del mes. Hacé clic en un día del mes para acotar la lista a ese día.'}
        </p>
        <BookingsListPanel
          bookings={listBookings}
          emptyMessage={listEmptyMessage}
          onCancel={
            canCancelBookings
              ? (booking) => {
                  setCancelTarget(booking);
                  setCancelReason('');
                }
              : undefined
          }
          cancellingId={cancellingId}
        />
      </div>
      <Modal
        isOpen={cancelTarget != null}
        onClose={() => {
          if (cancellingId) return;
          setCancelTarget(null);
          setCancelReason('');
        }}
        title="Cancelar reserva"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="ghost"
              size="md"
              disabled={Boolean(cancellingId)}
              onClick={() => {
                setCancelTarget(null);
                setCancelReason('');
              }}
            >
              Volver
            </Button>
            <Button
              variant="danger"
              size="md"
              disabled={Boolean(cancellingId)}
              onClick={() => void handleConfirmCancellation()}
            >
              {cancellingId ? 'Cancelando…' : 'Confirmar cancelación'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: '0 0 16px', color: tokens.colors.neutral[700] }}>
          ¿Querés cancelar la reserva de <strong>{cancelTarget?.customerName}</strong> en{' '}
          <strong>{cancelTarget?.resourceName}</strong>?
        </p>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>
          Motivo (opcional)
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={Boolean(cancellingId)}
            rows={3}
            maxLength={1000}
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              marginTop: 6,
              padding: 10,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              resize: 'vertical',
              font: 'inherit',
            }}
          />
        </label>
      </Modal>
    </div>
  );
}

export default CalendarViewPage;
